import type { ZodDto } from '@geartracker/dto';

import * as Sentry from '@sentry/react';
import type { z } from 'zod';

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

/**
 * Non-blocking safety net: checks that a payload about to be sent to the
 * backend actually matches the DTO it's supposed to conform to. This is a
 * different failure class than form validation — form schemas only catch
 * what the user typed, not bugs in the `xFormDataToDto` mapper functions
 * that build the payload from it.
 *
 * Always returns the exact `payload` it was given, untouched, on both the
 * success and failure path — a normal `safeParse` success is NOT enough on
 * its own: Zod's default object mode silently strips unrecognized keys
 * instead of failing (so a mistyped/renamed mapper field lands on
 * `result.success: true` if the correct field happens to be optional — the
 * bug would otherwise vanish with zero signal), and some DTOs `.transform()`
 * their input (e.g. computing a derived field, or reconciling two arrays),
 * which would silently rewrite the payload if we ever returned `result.data`.
 * This only diffs top-level keys — it won't catch a wrong value nested
 * inside an object/array field.
 */
export function assertDtoShape<TSchema extends z.ZodType, TPayload>(
  dto: ZodDto<TSchema>,
  payload: TPayload,
  context: string,
): TPayload {
  const result = dto.schema.safeParse(payload);

  if (!result.success) {
    Sentry.captureException(new Error(`Payload mismatch: ${context}`), {
      extra: {
        context,
        issues: result.error.issues,
        payload,
      },
    });
    console.error(`[dto-mismatch] ${context}`, result.error.issues);
    return payload;
  }

  if (isPlainObject(payload) && isPlainObject(result.data)) {
    const droppedKeys = Object.keys(payload).filter(
      (key) => !Object.hasOwn(result.data as object, key),
    );
    if (droppedKeys.length > 0) {
      Sentry.captureException(
        new Error(`Unrecognized payload keys silently dropped: ${context}`),
        { extra: { context, droppedKeys, payload } },
      );
      console.error(
        `[dto-mismatch] ${context}: unrecognized keys`,
        droppedKeys,
      );
    }
  }

  return payload;
}
